// PitchView.jsx
import React, { useState, useEffect } from 'react';
import {
  DndContext,
  useSensor,
  useSensors,
  PointerSensor,
  closestCenter,
  useDraggable,
  useDroppable,
} from '@dnd-kit/core';
import { Plus, X } from 'lucide-react';
import { POSITIONS } from '../../utils/constants';
import pitchImg from '../../assets/pitch.png';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { Textarea } from 'components/ui/textarea';
import { cn } from 'lib/utils';

const MAX_OUTFIELD_SLOTS_PER_ROW = 5;
const MAX_OUTFIELD_PLAYERS = 10;

/* ---------- helpers ---------- */

// Colour-codes the W/L/CS probability badges so GW difficulty is scannable.
// For win/CS a high value is good (green); for loss a high value is bad (red).
function probBadgeClass(value, kind) {
  if (!Number.isFinite(value)) return 'bg-white/20 text-white';
  const goodThreshold = kind === 'cs' ? 40 : 50;
  const badThreshold = kind === 'cs' ? 20 : 30;
  const good = kind === 'lose' ? value < badThreshold : value >= goodThreshold;
  const bad = kind === 'lose' ? value >= goodThreshold : value < badThreshold;
  if (good) return 'bg-success text-white';
  if (bad) return 'bg-error text-white';
  return 'bg-warning text-white';
}

const formatProb = (value) => (Number.isFinite(value) ? Math.round(value) : '–');

function PlayerCard({ player, shirtName, onAdd, onRemove, disabled }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: player.element.toString(),
  });

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      /* touch-none disables native scrolling so the drag can start */
      className={cn('relative w-[70px] touch-none text-center', isDragging && 'z-dragging')}
      style={
        transform ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` } : undefined
      }
    >
      <img
        src={require(`../../assets/Team=${shirtName}.png`)}
        alt={player.short_name}
        className="mx-auto block w-10"
      />

      {onAdd && (
        <button
          type="button"
          onClick={onAdd}
          disabled={disabled}
          aria-label={`Add ${player.web_name}`}
          className="absolute right-0 top-0 rounded-full bg-neutral-300 p-0.5 text-neutral-800 hover:bg-neutral-400 disabled:pointer-events-none disabled:opacity-50"
        >
          <Plus size={14} />
        </button>
      )}
      {onRemove && (
        <button
          type="button"
          onClick={() => onRemove(player)}
          aria-label={`Remove ${player.web_name}`}
          className="absolute right-0 top-0 rounded-full bg-white p-0.5 text-neutral-800 shadow hover:bg-neutral-200"
        >
          <X size={14} />
        </button>
      )}

      <div className="rounded-token-xs border border-brand-darkNavy bg-[#FCFFF9]">
        <div className="rounded-t-[3px] bg-brand-darkNavy p-px text-white">
          <p className="truncate p-px text-[12px] leading-none">{player.web_name}</p>
        </div>
        <p className="p-px text-[12px] leading-none">{player.singular_name_short}</p>
      </div>
    </div>
  );
}

function DroppableCell({ id, children }) {
  const { isOver, setNodeRef } = useDroppable({ id });
  return (
    <div
      ref={setNodeRef}
      /* touch-none: same reason as PlayerCard */
      className={cn(
        'relative touch-none border border-dotted border-white',
        isOver && 'bg-white/30 outline-dashed outline-2 outline-white'
      )}
      style={{ minHeight: children ? 60 : 10 }}
    >
      {children}
    </div>
  );
}

/* ---------- TeamPitch ---------- */

function TeamPitch({
  teamId,
  oppName,
  winProb,
  csProb,
  loseProb,
  fixtureOptaID,
  fixtureFplID,
  displayName,
  shirtName,
  initialPlayers = [],
  players,
  onLineupChange,
}) {
  /* state ------------------------------------------------------------- */
  const [inLineup, setInLineup] = useState({});
  const [positions, setPositions] = useState({});
  const [searchText, setSearchText] = useState('');

  /* seed state from initialPlayers ----------------------------------- */
  useEffect(() => {
    if (!initialPlayers.length) return;

    const lineupState = {};
    const posState = {};

    initialPlayers.forEach((p) => {
      const id = p.element.toString();
      lineupState[id] = { selected: true };
      posState[id] = { row: p.pos_row, col: p.pos_col };
    });

    setInLineup(lineupState);
    setPositions(posState);
  }, [initialPlayers]);

  /* sensors – Pointer covers mouse AND touch (mobile) ----------------- */
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    })
  );

  /* derived data ------------------------------------------------------ */
  const selectedIds = Object.keys(inLineup).filter((id) => inLineup[id]?.selected);

  const selectedPlayers = selectedIds
    .map((id) => players.find((p) => p.element.toString() === id))
    .filter(Boolean)
    .map((p) => ({
      ...p,
      fixtureOptaID,
      fixtureFplID,
      teamId,
    }));

  const selectedGkp = selectedPlayers.find((p) => p.singular_name_short === 'GKP');
  const selectedOut = selectedPlayers.filter((p) => p.singular_name_short !== 'GKP');

  /* push up changed lineup ------------------------------------------- */
  useEffect(() => {
    const payload = selectedPlayers.map((p) => ({
      element: p.element,
      opta_playerID: p.opta_playerID,
      team_fplID: p.teamId,
      team_optaID: p.teamId,
      fixture_optaID: p.fixtureOptaID,
      fixture_fplID: p.fixtureFplID,
      pos_col: positions[p.element]?.col,
      pos_row: positions[p.element]?.row,
    }));
    onLineupChange(payload);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(inLineup), JSON.stringify(positions)]);

  /* add / remove handlers -------------------------------------------- */
  const handleAdd = (p) => {
    const elId = p.element.toString();
    if (p.singular_name_short === 'GKP') {
      if (selectedGkp) return;
      setInLineup((prev) => ({ ...prev, [elId]: { selected: true } }));
      setPositions((prev) => ({ ...prev, [elId]: { row: 1, col: 3 } }));
      return;
    }
    if (selectedOut.length >= MAX_OUTFIELD_PLAYERS) return;
    setInLineup((prev) => ({ ...prev, [elId]: { selected: true } }));
    setPositions((prev) => {
      for (let r = 2; r <= 6; r++) {
        for (let c = 1; c <= MAX_OUTFIELD_SLOTS_PER_ROW; c++) {
          if (!Object.values(prev).some((pos) => pos.row === r && pos.col === c)) {
            return { ...prev, [elId]: { row: r, col: c } };
          }
        }
      }
      return prev;
    });
  };

  const handleRemove = (p) => {
    const elId = p.element.toString();
    setInLineup((prev) => ({ ...prev, [elId]: { selected: false } }));
    setPositions((prev) => {
      const next = { ...prev };
      delete next[elId];
      return next;
    });
  };

  /* drag-n-drop handlers --------------------------------------------- */
  const handleDragOver = () => {};
  const handleDragEnd = ({ active, over }) => {
    if (!over) return;
    const elId = active.id;
    if (!inLineup[elId]?.selected) return;
    const p = players.find((pl) => pl.element.toString() === elId);
    if (!p) return;

    if (over.id === 'gkp' && p.singular_name_short !== 'GKP') return;

    setPositions((prev) => {
      const next = { ...prev };
      if (over.id === 'gkp') {
        next[elId] = { row: 1, col: 3 };
      } else if (/^\d+_\d+$/.test(over.id)) {
        const [r, c] = over.id.split('_').map(Number);
        const swapId = Object.entries(next).find(([, pos]) => pos.row === r && pos.col === c)?.[0];
        next[elId] = { row: r, col: c };
        if (swapId) next[swapId] = prev[elId];
      }
      return next;
    });
  };

  /* available players list ------------------------------------------- */
  const search = searchText.trim().toLowerCase();
  const availablePlayers = players
    .filter((p) => !selectedIds.includes(p.element.toString()))
    .filter((p) => !search || (p.web_name || '').toLowerCase().includes(search))
    .sort(
      (a, b) =>
        (a.fpl_draft_rank ?? Infinity) - (b.fpl_draft_rank ?? Infinity) ||
        (a.web_name || '').localeCompare(b.web_name || '')
    );

  const grouped = POSITIONS.map((pos) => ({
    key: pos,
    players: availablePlayers.filter((p) => p.singular_name_short === pos),
  })).filter((g) => g.players.length);

  /* render ------------------------------------------------------------ */
  return (
    <div className="flex min-h-[400px] flex-wrap items-stretch gap-2">
      {/* --- pitch --- */}
      <div className="flex min-h-[400px] w-full max-w-[400px] flex-col rounded-token-md bg-pitchGreen">
        {/* header */}
        <div className="flex items-center justify-between gap-2 p-2">
          <div className="rounded-token-sm bg-white/20 p-2">
            <p className="text-token-lg font-bold leading-none text-white">{displayName}</p>
            <p className="mt-1 text-token-md italic leading-none text-white">vs. {oppName}</p>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-1">
            <span
              className={cn(
                'rounded-token-sm px-2 py-1 text-token-base font-semibold',
                selectedPlayers.length === 11 ? 'bg-success text-white' : 'bg-white/20 text-white'
              )}
              title="Players selected"
            >
              {selectedPlayers.length}/11
            </span>
            <span
              className={cn(
                'rounded-token-sm px-2 py-1 text-token-base font-semibold',
                probBadgeClass(winProb, 'win')
              )}
            >
              W: {formatProb(winProb)}%
            </span>
            <span
              className={cn(
                'rounded-token-sm px-2 py-1 text-token-base font-semibold',
                probBadgeClass(loseProb, 'lose')
              )}
            >
              L: {formatProb(loseProb)}%
            </span>
            <span
              className={cn(
                'rounded-token-sm px-2 py-1 text-token-base font-semibold',
                probBadgeClass(csProb, 'cs')
              )}
            >
              cs: {formatProb(csProb)}%
            </span>
          </div>
        </div>

        {/* body (pitch image) */}
        <div
          className="flex-auto overflow-hidden rounded-token-large p-2"
          style={{
            backgroundImage: `url(${pitchImg})`,
            backgroundPosition: 'top',
            backgroundRepeat: 'no-repeat',
            backgroundSize: '100% auto',
          }}
        >
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragOver={handleDragOver}
            onDragEnd={handleDragEnd}
          >
            {/* GK row */}
            <div className="mb-2 grid grid-cols-[repeat(5,minmax(10px,max-content))] justify-evenly gap-2">
              {Array.from({ length: MAX_OUTFIELD_SLOTS_PER_ROW }).map((_, idx) => {
                const col = idx + 1;
                if (col === 3) {
                  return (
                    <DroppableCell key="gkp" id="gkp">
                      {selectedGkp && (
                        <PlayerCard
                          player={selectedGkp}
                          shirtName={shirtName}
                          onRemove={handleRemove}
                        />
                      )}
                    </DroppableCell>
                  );
                }
                return (
                  <div
                    key={`gkp_slot_${col}`}
                    className="min-h-[10px] border border-dotted border-white/30"
                  />
                );
              })}
            </div>

            {/* outfield rows */}
            {[2, 3, 4, 5, 6].map((r) => (
              <div
                key={r}
                className="mb-2 grid grid-cols-[repeat(5,minmax(10px,max-content))] justify-evenly gap-2"
              >
                {Array.from({ length: MAX_OUTFIELD_SLOTS_PER_ROW }).map((_, idx) => {
                  const col = idx + 1;
                  const pl = selectedOut.find(
                    (p) => positions[p.element]?.row === r && positions[p.element]?.col === col
                  );
                  return (
                    <DroppableCell key={`${r}_${col}`} id={`${r}_${col}`}>
                      {pl && (
                        <PlayerCard player={pl} shirtName={shirtName} onRemove={handleRemove} />
                      )}
                    </DroppableCell>
                  );
                })}
              </div>
            ))}
          </DndContext>
        </div>
      </div>

      {/* --- available list --- */}
      <div className="flex min-h-[400px] min-w-[400px] flex-1 flex-col rounded-token-md bg-white p-2">
        <Input
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          placeholder="Search players..."
          className="mb-2 h-8"
        />
        <div className="flex-auto overflow-y-auto">
          {grouped.map((g) => (
            <div key={g.key} className="mb-2">
              <p className="text-token-md font-semibold">{g.key}</p>
              <div className="flex flex-wrap gap-3 py-1">
                {g.players.map((p) => (
                  <PlayerCard
                    key={p.element}
                    player={p}
                    shirtName={shirtName}
                    onAdd={() => handleAdd(p)}
                  />
                ))}
              </div>
              <div className="my-2 border-b border-borderGrey" />
            </div>
          ))}
          {grouped.length === 0 && (
            <p className="p-4 text-center text-token-md text-muted-foreground">
              {search ? 'No players match your search.' : 'No players available.'}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

/* ---------- Commentary Textarea with local state ---------- */
/* Local state + onBlur propagation prevents the parent Lineups component from
   re-rendering on every keystroke. Do not change this pattern. */

function CommentaryTextField({ label, placeholder, initialValue, onBlur }) {
  const [localValue, setLocalValue] = useState(initialValue || '');

  // Sync with parent value when it changes externally (e.g., initial load)
  useEffect(() => {
    setLocalValue(initialValue || '');
  }, [initialValue]);

  return (
    <div className="mt-2">
      <Label className="text-token-base text-muted-foreground">{label}</Label>
      <Textarea
        rows={3}
        placeholder={placeholder}
        value={localValue}
        onChange={(e) => setLocalValue(e.target.value)}
        onBlur={() => onBlur(localValue)}
        className="mt-1"
      />
    </div>
  );
}

/* ---------- outer wrapper ---------- */

export default function PitchView({
  fixture,
  teams,
  odds,
  elementsByTeam,
  initialLineups,
  onLineupChange,
  commentary = {},
  onCommentaryChange,
}) {
  const { fixture_fplID, fixture_optaID } = fixture;
  const sides = [
    { id: fixture.team_a, label: '(H)' },
    { id: fixture.team_h, label: '(A)' },
  ];

  return (
    <div className="flex flex-wrap gap-4">
      {sides.map(({ id, label }) => {
        const team = teams.find((t) => t.team_fplID === id) || {};
        const otherFplID = id === fixture.team_a ? fixture.team_h : fixture.team_a;
        const oppTeam = teams.find((t) => t.team_fplID === otherFplID) || {};
        const oppName = oppTeam.name || otherFplID;
        const myOdds =
          odds.find(
            (o) => o.fixture_optaID === fixture.fixture_optaID && o.team_optaID === team.team_optaID
          ) || {};
        const { win_prob, cs_prob, lose_prob } = myOdds;

        const baseName = team.name || id;
        const displayName = `${baseName} ${label}`;
        const shirtName = baseName;
        const players = elementsByTeam[id] || [];
        const preloaded = initialLineups[id]?.players || [];
        const teamCommentary = commentary[id] || '';

        return (
          <div key={id} className="rounded-token-large border border-borderGrey bg-white p-2">
            <TeamPitch
              teamId={id}
              oppName={oppName}
              winProb={win_prob}
              csProb={cs_prob}
              loseProb={lose_prob}
              fixtureOptaID={fixture_optaID}
              fixtureFplID={fixture_fplID}
              displayName={displayName}
              shirtName={shirtName}
              initialPlayers={preloaded}
              players={players}
              onLineupChange={(playersArray) => onLineupChange(id, playersArray)}
            />
            {/* Commentary text field - uses local state to prevent lag */}
            <CommentaryTextField
              label={`${baseName} Commentary`}
              placeholder={`Add commentary for ${baseName}...`}
              initialValue={teamCommentary}
              onBlur={(text) =>
                onCommentaryChange && onCommentaryChange(fixture_optaID, id, team.team_optaID, text)
              }
            />
          </div>
        );
      })}
    </div>
  );
}
